import React, { useCallback, useEffect, useRef, useState } from "react";

import { Button } from "./ui/button";

/**
 * Tiny finger / stylus / mouse signature pad. Emits a base64 PNG data
 * URL on demand via the imperative `onCapture` callback the parent
 * passes in (rather than a controlled `value` prop) so the parent
 * grabs the bitmap exactly at submit time.
 *
 * No external dependency — straight HTML5 canvas, ~80 LOC. Built for
 * the manual-checkout flow (Jason 2026-06-18) where the legacy PWA
 * used a signature_pad library; canvas alone is enough for our needs.
 */
interface Props {
  /** Logical width in CSS pixels. Canvas resolution is 2× for sharpness on retina. */
  width?: number;
  height?: number;
  /** Called with `null` if the user clicks Clear; otherwise the latest data URL. */
  onChange?: (dataUrl: string | null) => void;
  className?: string;
}

export const SignaturePad: React.FC<Props> = ({
  width = 480,
  height = 160,
  onChange,
  className,
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawingRef = useRef(false);
  const lastRef = useRef<{ x: number; y: number } | null>(null);
  const [hasInk, setHasInk] = useState(false);

  // Set up the canvas DPI scaling. 2× backing store, CSS-sized to the
  // logical width/height so the stroke stays crisp on retina.
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const dpr = window.devicePixelRatio || 1;
    c.width = Math.round(width * dpr);
    c.height = Math.round(height * dpr);
    c.style.width = `${width}px`;
    c.style.height = `${height}px`;
    const ctx = c.getContext("2d");
    if (!ctx) return;
    ctx.scale(dpr, dpr);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = 2;
    ctx.strokeStyle = "#0f172a";  // slate-900 — high contrast on white
  }, [width, height]);

  const posFrom = (e: PointerEvent | React.PointerEvent): { x: number; y: number } => {
    const c = canvasRef.current!;
    const rect = c.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const emit = useCallback(() => {
    const c = canvasRef.current;
    if (!c || !onChange) return;
    onChange(hasInk ? c.toDataURL("image/png") : null);
  }, [hasInk, onChange]);

  const onPointerDown = (e: React.PointerEvent<HTMLCanvasElement>): void => {
    drawingRef.current = true;
    lastRef.current = posFrom(e);
    (e.target as HTMLCanvasElement).setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e: React.PointerEvent<HTMLCanvasElement>): void => {
    if (!drawingRef.current) return;
    const c = canvasRef.current;
    const ctx = c?.getContext("2d");
    if (!c || !ctx || !lastRef.current) return;
    const p = posFrom(e);
    ctx.beginPath();
    ctx.moveTo(lastRef.current.x, lastRef.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastRef.current = p;
    if (!hasInk) setHasInk(true);
  };

  const onPointerUp = (): void => {
    drawingRef.current = false;
    lastRef.current = null;
    emit();
  };

  const clear = (): void => {
    const c = canvasRef.current;
    const ctx = c?.getContext("2d");
    if (!c || !ctx) return;
    const dpr = window.devicePixelRatio || 1;
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, c.width, c.height);
    ctx.restore();
    setHasInk(false);
    onChange?.(null);
  };

  return (
    <div className={className}>
      <div className="border border-secondary-300 rounded bg-white inline-block">
        <canvas
          ref={canvasRef}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerLeave={() => {
            if (drawingRef.current) onPointerUp();
          }}
          style={{ touchAction: "none", cursor: "crosshair" }}
        />
      </div>
      <div className="flex items-center gap-2 mt-1">
        <Button
          type="button" size="sm" variant="outline"
          onClick={clear} disabled={!hasInk}
        >
          Clear
        </Button>
        <span className="text-[11px] text-secondary-500">
          {hasInk ? "✓ signature captured" : "draw with finger / stylus / mouse"}
        </span>
      </div>
    </div>
  );
};

export default SignaturePad;
